import React, { useState, useEffect } from 'react';
import { t } from '../lib/i18n';
import { analyzeProject, analyzeProjectDocument, listProjectAnalyses, deleteProjectAnalysis, getLightingNorms, calculateRoom } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Lightbulb, Plus, Trash2, ArrowLeft, Sparkles, Loader2, Eye, Upload,
  Sun, Zap, Package, ChevronDown, ChevronRight, Building2, Calculator,
  FileText, BarChart3, AlertTriangle, CheckCircle2, Ruler, Clock,
  ShieldCheck, ShieldAlert, ShieldX, Info, XCircle
} from 'lucide-react';

const ROOM_TYPE_GROUPS = {
  office: {
    label: { pl: 'Biurowe', en: 'Office' },
    types: ['office_general', 'office_technical', 'office_reception', 'office_archive', 'office_conference', 'office_corridor']
  },
  production: {
    label: { pl: 'Produkcyjne', en: 'Production' },
    types: ['production_general', 'production_precision', 'production_inspection', 'warehouse', 'warehouse_intensive', 'workshop']
  },
  retail: {
    label: { pl: 'Handlowe', en: 'Retail' },
    types: ['shop_general', 'shop_checkout', 'showroom', 'display_area']
  },
  education: {
    label: { pl: 'Edukacyjne', en: 'Education' },
    types: ['classroom', 'classroom_evening', 'lecture_hall', 'laboratory', 'library_reading', 'library_shelves']
  },
  healthcare: {
    label: { pl: 'Medyczne', en: 'Healthcare' },
    types: ['hospital_general', 'hospital_examination', 'hospital_operating', 'hospital_corridor', 'pharmacy']
  },
  hospitality: {
    label: { pl: 'Hotelowe / Gastronomia', en: 'Hospitality' },
    types: ['restaurant', 'kitchen_commercial', 'hotel_room', 'hotel_lobby']
  },
  common: {
    label: { pl: 'Komunikacja / Wspolne', en: 'Common Areas' },
    types: ['staircase', 'parking_indoor', 'bathroom_public']
  },
  sport: {
    label: { pl: 'Sport / Rekreacja', en: 'Sport / Recreation' },
    types: ['gym', 'sports_hall', 'swimming_pool']
  },
  outdoor: {
    label: { pl: 'Zewnetrzne', en: 'Outdoor' },
    types: ['outdoor_parking', 'outdoor_walkway', 'outdoor_loading', 'facade']
  },
};

export default function ProjectAnalyzer({ lang, tenantId }) {
  const [view, setView] = useState('list');
  const [analyses, setAnalyses] = useState([]);
  const [norms, setNorms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [expandedRooms, setExpandedRooms] = useState({});

  // Form state
  const [description, setDescription] = useState('');
  const [rooms, setRooms] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  useEffect(() => { loadData(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadData() {
    setLoading(true);
    try {
      const [a, n] = await Promise.all([
        listProjectAnalyses(tenantId),
        getLightingNorms().catch(() => null)
      ]);
      setAnalyses(a);
      setNorms(n);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  function addRoom() {
    setRooms(prev => [...prev, { name: '', type: 'office_general', area_m2: 20, height_m: 2.8 }]);
  }

  function updateRoom(idx, field, value) {
    setRooms(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  }

  function removeRoom(idx) {
    setRooms(prev => prev.filter((_, i) => i !== idx));
  }

  function getNormName(typeKey) {
    if (!norms) return typeKey;
    const norm = norms.norms?.[typeKey];
    return norm ? (lang === 'pl' ? norm.name_pl : norm.name_en) : typeKey;
  }

  async function handleAnalyze() {
    if (!description.trim() && rooms.length === 0) {
      toast.error(lang === 'pl' ? 'Podaj opis projektu lub dodaj pomieszczenia' : 'Provide description or add rooms');
      return;
    }
    setAnalyzing(true);
    try {
      const result = await analyzeProject({ description, rooms }, tenantId);
      toast.success(lang === 'pl' ? 'Analiza zako\u0144czona!' : 'Analysis complete!');
      setSelectedAnalysis(result);
      setView('detail');
      loadData();
    } catch (err) {
      toast.error(lang === 'pl' ? 'B\u0142\u0105d analizy' : 'Analysis failed');
    }
    setAnalyzing(false);
  }

  async function handleDelete(id) {
    try {
      await deleteProjectAnalysis(id);
      toast.success(lang === 'pl' ? 'Usunieto' : 'Deleted');
      setAnalyses(prev => prev.filter(a => a.id !== id));
      if (selectedAnalysis?.analysis_id === id) {
        setView('list');
        setSelectedAnalysis(null);
      }
    } catch (err) {
      toast.error('Error');
    }
  }

  async function handleDocumentUpload() {
    if (!uploadFile) {
      toast.error(lang === 'pl' ? 'Wybierz plik' : 'Select a file');
      return;
    }
    setUploadingDoc(true);
    try {
      const result = await analyzeProjectDocument(uploadFile, tenantId, description);
      toast.success(lang === 'pl' ? 'Dokument przeanalizowany!' : 'Document analyzed!');
      setSelectedAnalysis({ result });
      setView('document_report');
      loadData();
    } catch (err) {
      console.error(err);
      toast.error(lang === 'pl' ? 'Blad analizy dokumentu' : 'Document analysis failed');
    }
    setUploadingDoc(false);
  }

  function toggleRoom(idx) {
    setExpandedRooms(prev => ({ ...prev, [idx]: !prev[idx] }));
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 w-full" />)}
        </div>
      </div>
    );
  }

  // ===== DOCUMENT COMPLIANCE REPORT =====
  if (view === 'document_report' && selectedAnalysis) {
    const docResult = selectedAnalysis.result || {};
    const extraction = docResult.extraction || {};
    const compliance = docResult.compliance || {};
    const roomChecks = compliance.room_checks || [];
    const issues = compliance.issues || [];

    const statusConfig = {
      compliant: { icon: ShieldCheck, color: 'text-green-400', bg: 'bg-green-400/10 border-green-400/20', label: lang === 'pl' ? 'Zgodny z normami' : 'Compliant' },
      partially_compliant: { icon: ShieldAlert, color: 'text-amber-400', bg: 'bg-amber-400/10 border-amber-400/20', label: lang === 'pl' ? 'Czesciowo zgodny' : 'Partially Compliant' },
      non_compliant: { icon: ShieldX, color: 'text-red-400', bg: 'bg-red-400/10 border-red-400/20', label: lang === 'pl' ? 'Niezgodny' : 'Non-Compliant' },
      insufficient_data: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-400/10 border-blue-400/20', label: lang === 'pl' ? 'Niewystarczajace dane' : 'Insufficient Data' },
    };
    const statusInfo = statusConfig[compliance.overall_status] || statusConfig.insufficient_data;
    const StatusIcon = statusInfo.icon;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setView('list'); setSelectedAnalysis(null); }}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            {lang === 'pl' ? 'Wroc' : 'Back'}
          </Button>
          <div>
            <h1 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
              {lang === 'pl' ? 'Raport zgodnosci oswietlenia' : 'Lighting Compliance Report'}
            </h1>
            <p className="text-xs text-muted-foreground">
              {docResult.filename} | {docResult.pages_analyzed} {lang === 'pl' ? 'stron' : 'pages'} |{' '}
              {extraction.confidence ? `${(extraction.confidence * 100).toFixed(0)}% ` : ''}
              {lang === 'pl' ? 'pewnosci odczytu' : 'read confidence'}
            </p>
          </div>
        </div>

        {/* Overall Status */}
        <Card className={`border ${statusInfo.bg}`}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className={`w-16 h-16 rounded-2xl ${statusInfo.bg} flex items-center justify-center`}>
              <StatusIcon className={`w-8 h-8 ${statusInfo.color}`} />
            </div>
            <div className="flex-1">
              <p className={`text-lg font-bold ${statusInfo.color}`}>{statusInfo.label}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {lang === 'pl'
                  ? `Ocena: ${compliance.score || 0}/100 | ${compliance.rooms_checked || 0} pomieszczen sprawdzonych | ${compliance.critical_issues || 0} problemow krytycznych | ${compliance.warnings || 0} ostrzezen`
                  : `Score: ${compliance.score || 0}/100 | ${compliance.rooms_checked || 0} rooms checked | ${compliance.critical_issues || 0} critical | ${compliance.warnings || 0} warnings`}
              </p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
                <span className={statusInfo.color}>{compliance.score || 0}</span>
                <span className="text-sm text-muted-foreground font-normal">/100</span>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Document Extraction Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <FileText className="w-5 h-5 mx-auto mb-1 text-primary" />
              <p className="text-sm font-semibold">{extraction.document_type || '-'}</p>
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Typ dokumentu' : 'Document Type'}</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <Building2 className="w-5 h-5 mx-auto mb-1 text-blue-400" />
              <p className="text-sm font-semibold">{(extraction.rooms_found || []).length}</p>
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Pomieszczen' : 'Rooms Found'}</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <Lightbulb className="w-5 h-5 mx-auto mb-1 text-amber-400" />
              <p className="text-sm font-semibold">{(extraction.fixtures_found || []).length}</p>
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Opraw wykrytych' : 'Fixtures Found'}</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <Eye className="w-5 h-5 mx-auto mb-1 text-green-400" />
              <p className="text-sm font-semibold">{extraction.readability || '-'}</p>
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Czytelnosc' : 'Readability'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Extracted Fixtures */}
        {extraction.fixtures_found && extraction.fixtures_found.length > 0 && (
          <Card className="border border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-400" />
                {lang === 'pl' ? 'Wykryte oprawy w projekcie' : 'Fixtures Detected in Project'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {extraction.fixtures_found.map((fx, i) => (
                  <div key={i} className="p-3 rounded-lg bg-muted/20 border border-border flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-400/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Lightbulb className="w-4 h-4 text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{fx.manufacturer || ''} {fx.model || ''}</span>
                        {fx.symbol_on_drawing && (
                          <Badge variant="outline" className="text-xs">{fx.symbol_on_drawing}</Badge>
                        )}
                        {fx.quantity > 0 && (
                          <Badge variant="secondary" className="text-xs">x{fx.quantity}</Badge>
                        )}
                      </div>
                      {fx.room && <p className="text-xs text-muted-foreground mt-0.5">{lang === 'pl' ? 'Pomieszczenie' : 'Room'}: {fx.room}</p>}
                      <div className="flex gap-3 mt-1 flex-wrap">
                        {fx.specs?.lumens > 0 && <span className="text-xs text-muted-foreground">{fx.specs.lumens} lm</span>}
                        {fx.specs?.watts > 0 && <span className="text-xs text-muted-foreground">{fx.specs.watts} W</span>}
                        {fx.specs?.kelvin > 0 && <span className="text-xs text-muted-foreground">{fx.specs.kelvin} K</span>}
                        {fx.specs?.cri > 0 && <span className="text-xs text-muted-foreground">Ra {fx.specs.cri}</span>}
                        {fx.specs?.ugr > 0 && <span className="text-xs text-muted-foreground">UGR {fx.specs.ugr}</span>}
                        {fx.specs?.ip && <span className="text-xs text-muted-foreground">{fx.specs.ip}</span>}
                        {fx.specs?.type && <Badge variant="secondary" className="text-xs py-0">{fx.specs.type}</Badge>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Room-by-Room Compliance Checks */}
        {roomChecks.length > 0 && (
          <Card className="border border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-primary" />
                {lang === 'pl' ? 'Weryfikacja zgodnosci per pomieszczenie' : 'Per-Room Compliance Check'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {roomChecks.map((rc, i) => {
                const isExpanded = expandedRooms[`comp_${i}`];
                const checks = rc.checks || {};
                const allPass = Object.values(checks).every(v => v === 'pass' || v === 'not_applicable' || v === 'no_data');
                const hasFail = Object.values(checks).some(v => v === 'fail');

                return (
                  <div key={i} className="border border-border rounded-xl overflow-hidden">
                    <button
                      onClick={() => setExpandedRooms(prev => ({ ...prev, [`comp_${i}`]: !prev[`comp_${i}`] }))}
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        hasFail ? 'bg-red-400/10' : allPass ? 'bg-green-400/10' : 'bg-amber-400/10'
                      }`}>
                        {hasFail
                          ? <XCircle className="w-4 h-4 text-red-400" />
                          : allPass
                          ? <CheckCircle2 className="w-4 h-4 text-green-400" />
                          : <AlertTriangle className="w-4 h-4 text-amber-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{rc.room_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {rc.norm?.norm_name} | {rc.area_m2} m2 | {rc.fixtures_count} {lang === 'pl' ? 'opraw' : 'fixtures'}
                        </p>
                      </div>
                      {/* Check badges */}
                      <div className="flex gap-1.5 flex-shrink-0">
                        {Object.entries(checks).map(([key, val]) => {
                          if (val === 'not_applicable') return null;
                          const colors = { pass: 'bg-green-400/10 text-green-400', fail: 'bg-red-400/10 text-red-400',
                            warning: 'bg-amber-400/10 text-amber-400', no_data: 'bg-muted text-muted-foreground' };
                          return (
                            <Badge key={key} variant="outline" className={`text-xs ${colors[val] || colors.no_data}`}>
                              {key.toUpperCase()}
                            </Badge>
                          );
                        })}
                      </div>
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
                        {/* Norm requirements */}
                        <div className="grid grid-cols-4 gap-3">
                          <div className="p-2 rounded-lg bg-muted/30 text-center">
                            <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Wymagane' : 'Required'}</p>
                            <p className="text-sm font-semibold text-amber-400">{rc.norm?.required_lux} lux</p>
                          </div>
                          <div className="p-2 rounded-lg bg-muted/30 text-center">
                            <p className="text-xs text-muted-foreground">UGR max</p>
                            <p className="text-sm font-semibold">{rc.norm?.max_ugr ?? '-'}</p>
                          </div>
                          <div className="p-2 rounded-lg bg-muted/30 text-center">
                            <p className="text-xs text-muted-foreground">Ra min</p>
                            <p className="text-sm font-semibold">{rc.norm?.min_ra ?? '-'}</p>
                          </div>
                          <div className="p-2 rounded-lg bg-muted/30 text-center">
                            <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Strumien' : 'Lumens'}</p>
                            <p className="text-sm font-semibold">{(rc.total_lumens || 0).toLocaleString()} lm</p>
                          </div>
                        </div>
                        {/* Issues for this room */}
                        {(rc.issues || []).length > 0 && (
                          <div className="space-y-1.5">
                            {rc.issues.map((iss, j) => {
                              const sevConfig = {
                                critical: { bg: 'bg-red-400/5 border-red-400/20', icon: XCircle, color: 'text-red-400' },
                                warning: { bg: 'bg-amber-400/5 border-amber-400/20', icon: AlertTriangle, color: 'text-amber-400' },
                                info: { bg: 'bg-blue-400/5 border-blue-400/20', icon: Info, color: 'text-blue-400' },
                              };
                              const sc = sevConfig[iss.severity] || sevConfig.info;
                              const IssIcon = sc.icon;
                              return (
                                <div key={j} className={`flex items-start gap-2 p-2.5 rounded-lg border ${sc.bg}`}>
                                  <IssIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${sc.color}`} />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs">{iss.description}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">{iss.norm_reference}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {(rc.issues || []).length === 0 && (
                          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-green-400/5 border border-green-400/20 text-xs">
                            <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                            {lang === 'pl' ? 'Pomieszczenie spelnia wymogi normowe' : 'Room meets norm requirements'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Recommendations */}
        {compliance.recommendations && compliance.recommendations.length > 0 && (
          <Card className="border border-primary/20 bg-primary/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                {lang === 'pl' ? 'Rekomendacje' : 'Recommendations'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-2">
                {compliance.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="text-primary mt-0.5">•</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Overall Notes from AI */}
        {extraction.overall_notes && (
          <Card className="border border-border">
            <CardContent className="p-4 flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  {lang === 'pl' ? 'Uwagi z analizy dokumentu' : 'Document Analysis Notes'}
                </p>
                <p className="text-sm">{extraction.overall_notes}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Applied Norms */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <FileText className="w-3.5 h-3.5" />
          {lang === 'pl' ? 'Normy zastosowane' : 'Norms applied'}:
          {(compliance.norms_applied || []).map((n, i) => (
            <Badge key={i} variant="outline" className="text-xs">{n}</Badge>
          ))}
        </div>
      </div>
    );
  }

  // ===== DETAIL VIEW =====
  if (view === 'detail' && selectedAnalysis) {
    const result = selectedAnalysis.result || selectedAnalysis;
    const summary = result.summary || {};
    const roomList = result.rooms || [];

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setView('list'); setSelectedAnalysis(null); }}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            {lang === 'pl' ? 'Wr\u00f3\u0107' : 'Back'}
          </Button>
          <h1 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {lang === 'pl' ? 'Analiza o\u015bwietleniowa' : 'Lighting Analysis'}
          </h1>
          <Badge variant="secondary" className="text-xs">
            PN-EN 12464-1:2021
          </Badge>
        </div>

        {/* AI Summary */}
        {result.ai_summary && (
          <Card className="border border-primary/20 bg-primary/5">
            <CardContent className="p-5 flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-primary mb-1">
                  {lang === 'pl' ? 'Ekspertyza AI' : 'AI Expert Analysis'}
                </p>
                <p className="text-sm">{result.ai_summary}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <Building2 className="w-5 h-5 mx-auto mb-1 text-primary" />
              <p className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{roomList.length}</p>
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Pomieszczen' : 'Rooms'}</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <Sun className="w-5 h-5 mx-auto mb-1 text-amber-400" />
              <p className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
                {(summary.total_lumens || 0).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">lm</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <Zap className="w-5 h-5 mx-auto mb-1 text-blue-400" />
              <p className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{summary.total_watts || 0} W</p>
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Moc calkowita' : 'Total power'}</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <Lightbulb className="w-5 h-5 mx-auto mb-1 text-green-400" />
              <p className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{summary.total_fixtures || 0}</p>
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Opraw' : 'Fixtures'}</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <BarChart3 className="w-5 h-5 mx-auto mb-1 text-orange-400" />
              <p className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
                {(summary.estimated_annual_cost_pln || 0).toLocaleString()} PLN
              </p>
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Koszt roczny (est.)' : 'Annual cost (est.)'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Room Analyses */}
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Building2 className="w-4 h-4 text-primary" />
              {lang === 'pl' ? 'Analiza pomieszczen' : 'Room Analysis'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            {roomList.map((room, i) => {
              const isExpanded = expandedRooms[i];
              return (
                <div key={i} className="border border-border rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleRoom(i)}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{room.room_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {room.area_m2} m2 | {room.required_lux} lux | {room.fixture_count} {lang === 'pl' ? 'opraw' : 'fixtures'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge variant="secondary" className="text-xs">{room.convection?.name}</Badge>
                      {isExpanded
                        ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-4 border-t border-border pt-3">
                      {/* Norm Parameters */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <div className="p-2 rounded-lg bg-muted/30 text-center">
                          <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Wymagane' : 'Required'}</p>
                          <p className="text-sm font-semibold text-amber-400">{room.required_lux} lux</p>
                        </div>
                        <div className="p-2 rounded-lg bg-muted/30 text-center">
                          <p className="text-xs text-muted-foreground">UGR max</p>
                          <p className="text-sm font-semibold">{room.max_ugr || '-'}</p>
                        </div>
                        <div className="p-2 rounded-lg bg-muted/30 text-center">
                          <p className="text-xs text-muted-foreground">Ra min</p>
                          <p className="text-sm font-semibold">{room.min_ra || '-'}</p>
                        </div>
                        <div className="p-2 rounded-lg bg-muted/30 text-center">
                          <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Strumien' : 'Lumens'}</p>
                          <p className="text-sm font-semibold">{(room.total_lumens || 0).toLocaleString()} lm</p>
                        </div>
                        <div className="p-2 rounded-lg bg-muted/30 text-center">
                          <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Moc' : 'Power'}</p>
                          <p className="text-sm font-semibold">{room.total_watts} W</p>
                        </div>
                      </div>

                      {/* Convection & Style */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="p-3 rounded-lg bg-muted/20 border border-border">
                          <p className="text-xs font-medium text-muted-foreground mb-1">
                            {lang === 'pl' ? 'Konwekcja swiatla' : 'Light Convection'}
                          </p>
                          <p className="text-sm font-semibold">{room.convection?.name}</p>
                          <p className="text-xs text-muted-foreground mt-1">{room.convection?.description}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/20 border border-border">
                          <p className="text-xs font-medium text-muted-foreground mb-1">
                            {lang === 'pl' ? 'Rekomendowany styl' : 'Recommended Style'}
                          </p>
                          <p className="text-sm font-semibold">
                            {lang === 'pl' ? room.recommended_style?.name_pl : room.recommended_style?.name_en}
                          </p>
                          <div className="flex gap-1 mt-1">
                            {(room.recommended_style?.tags || []).map((tag, j) => (
                              <Badge key={j} variant="secondary" className="text-xs">{tag}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Layout Info */}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Ruler className="w-3.5 h-3.5" />
                          {lang === 'pl' ? 'Rozstaw' : 'Spacing'}: {room.spacing_m} m
                        </span>
                        <span className="flex items-center gap-1">
                          <Lightbulb className="w-3.5 h-3.5" />
                          {room.lumens_per_fixture} lm/{lang === 'pl' ? 'oprawa' : 'fixture'}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="w-3.5 h-3.5" />
                          {room.norm_reference}
                        </span>
                      </div>

                      {/* Matched Products */}
                      {room.matched_products && room.matched_products.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                            <Package className="w-3.5 h-3.5" />
                            {lang === 'pl' ? 'Dopasowane produkty z katalogu' : 'Matched catalog products'}
                          </p>
                          <div className="space-y-1.5">
                            {room.matched_products.map((prod, j) => (
                              <div key={j} className="flex items-center justify-between p-2.5 rounded-lg bg-green-400/5 border border-green-400/20">
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium">{prod.product_name}</p>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <Badge variant="secondary" className="text-xs">{prod.category}</Badge>
                                    {prod.reasons.map((r, k) => (
                                      <span key={k} className="text-xs text-green-400 flex items-center gap-0.5">
                                        <CheckCircle2 className="w-3 h-3" /> {r}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0 ml-3">
                                  <p className="text-sm font-semibold">{prod.base_price?.toLocaleString()} {prod.currency}</p>
                                  <Badge variant="secondary" className="text-xs bg-green-400/10 text-green-400">
                                    {lang === 'pl' ? 'Zgodnosc' : 'Match'}: {prod.score}%
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {room.matched_products && room.matched_products.length === 0 && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-400/5 border border-amber-400/20 text-xs">
                          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                          {lang === 'pl'
                            ? 'Brak dopasowanych produktow w katalogu. Dodaj oprawy o\u015bwietleniowe do katalogu produkt\u00f3w.'
                            : 'No matching products in catalog. Add lighting fixtures to your product catalog.'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ===== CREATE VIEW =====
  if (view === 'create') {
    return (
      <div className="space-y-6 max-w-3xl">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setView('list')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            {lang === 'pl' ? 'Wr\u00f3\u0107' : 'Back'}
          </Button>
          <h1 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {lang === 'pl' ? 'Nowa analiza projektu' : 'New Project Analysis'}
          </h1>
        </div>

        {/* Description */}
        <Card className="border border-border">
          <CardContent className="p-6 space-y-4">
            <div>
              <Label className="text-xs flex items-center gap-1.5">
                <FileText className="w-3 h-3" />
                {lang === 'pl' ? 'Opis projektu budowlanego' : 'Building project description'}
              </Label>
              <Textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="mt-1"
                rows={4}
                placeholder={lang === 'pl'
                  ? 'np. Biurowiec 3-pietrowy, open-space na parterze (200m2, h=3.0m), 6 gabinetow po 15m2, sala konferencyjna 40m2, korytarze, klatka schodowa, parking podziemny...'
                  : 'e.g. 3-floor office building, open-space on ground floor (200m2, h=3.0m), 6 offices at 15m2 each, conference room 40m2, corridors, staircase, underground parking...'}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {lang === 'pl'
                  ? 'AI automatycznie wyodrzbni pomieszczenia z opisu. Mozesz tez dodac je recznie ponizej.'
                  : 'AI will automatically extract rooms from description. You can also add them manually below.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Document Upload */}
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Upload className="w-4 h-4 text-primary" />
              {lang === 'pl' ? 'Wgraj rysunek techniczny (PDF, DXF, skan)' : 'Upload technical drawing (PDF, DXF, scan)'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <div
              className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer hover:border-primary/40 ${
                uploadFile ? 'border-primary/30 bg-primary/5' : 'border-border'
              }`}
              onClick={() => document.getElementById('doc-upload-input').click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary/40'); }}
              onDragLeave={e => { e.currentTarget.classList.remove('border-primary/40'); }}
              onDrop={e => {
                e.preventDefault();
                e.currentTarget.classList.remove('border-primary/40');
                const file = e.dataTransfer.files[0];
                if (file) setUploadFile(file);
              }}
            >
              <input
                id="doc-upload-input"
                type="file"
                className="hidden"
                accept=".pdf,.dxf,.dwg,.png,.jpg,.jpeg,.tiff,.bmp,.webp"
                onChange={e => setUploadFile(e.target.files[0] || null)}
              />
              {uploadFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-primary" />
                  <div className="text-left">
                    <p className="text-sm font-medium">{uploadFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={e => { e.stopPropagation(); setUploadFile(null); }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {lang === 'pl'
                      ? 'Przeciagnij plik lub kliknij aby wybrac'
                      : 'Drag & drop or click to select'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    PDF, DXF, DWG, PNG, JPG (max 50MB)
                  </p>
                </>
              )}
            </div>
            {uploadFile && (
              <Button
                onClick={handleDocumentUpload}
                disabled={uploadingDoc}
                className="w-full gap-2"
              >
                {uploadingDoc ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {lang === 'pl' ? 'Analizuje rysunek...' : 'Analyzing drawing...'}</>
                ) : (
                  <><Eye className="w-4 h-4" /> {lang === 'pl' ? 'Analizuj rysunek i sprawdz zgodnosc z normami' : 'Analyze drawing & check norm compliance'}</>
                )}
              </Button>
            )}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/30 border border-border text-xs text-muted-foreground">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-foreground">{lang === 'pl' ? 'AI Vision rozpoznaje:' : 'AI Vision recognizes:'}</p>
                <p>{lang === 'pl'
                  ? 'Rzuty architektoniczne, rysunki instalacji elektrycznej, specyfikacje opraw, zestawienia materiałowe, skany projektow'
                  : 'Architectural plans, electrical installation drawings, fixture specifications, bill of materials, project scans'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Manual Room List */}
        <Card className="border border-border">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Building2 className="w-4 h-4 text-primary" />
                {lang === 'pl' ? 'Pomieszczenia (opcjonalnie)' : 'Rooms (optional)'}
              </CardTitle>
              <Button size="sm" variant="secondary" onClick={addRoom} className="gap-1">
                <Plus className="w-3.5 h-3.5" />
                {lang === 'pl' ? 'Dodaj' : 'Add'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {rooms.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">
                {lang === 'pl'
                  ? 'Brak recznych pomieszczen. AI wykryje je z opisu lub dodaj je tutaj.'
                  : 'No manual rooms. AI will detect them from description or add them here.'}
              </p>
            )}
            {rooms.map((room, i) => (
              <div key={i} className="p-3 rounded-lg bg-muted/30 border border-border space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 grid grid-cols-4 gap-2">
                    <div>
                      <Label className="text-xs">{lang === 'pl' ? 'Nazwa' : 'Name'}</Label>
                      <Input
                        value={room.name}
                        onChange={e => updateRoom(i, 'name', e.target.value)}
                        className="mt-0.5"
                        placeholder={lang === 'pl' ? 'np. Biuro A' : 'e.g. Office A'}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{lang === 'pl' ? 'Typ' : 'Type'}</Label>
                      <select
                        value={room.type}
                        onChange={e => updateRoom(i, 'type', e.target.value)}
                        className="mt-0.5 w-full rounded-xl border border-border bg-background px-2 py-2 text-xs"
                      >
                        {Object.entries(ROOM_TYPE_GROUPS).map(([groupKey, group]) => (
                          <optgroup key={groupKey} label={group.label[lang] || group.label.en}>
                            {group.types.map(t => (
                              <option key={t} value={t}>{getNormName(t)}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">{lang === 'pl' ? 'Powierzchnia (m2)' : 'Area (m2)'}</Label>
                      <Input
                        type="number"
                        value={room.area_m2}
                        onChange={e => updateRoom(i, 'area_m2', parseFloat(e.target.value) || 0)}
                        className="mt-0.5"
                        min="1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{lang === 'pl' ? 'Wysokosc (m)' : 'Height (m)'}</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={room.height_m}
                        onChange={e => updateRoom(i, 'height_m', parseFloat(e.target.value) || 2.8)}
                        className="mt-0.5"
                        min="2"
                      />
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="mt-4 h-8 w-8 p-0 text-red-400" onClick={() => removeRoom(i)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Analyze Button */}
        <Button
          onClick={handleAnalyze}
          disabled={analyzing || (!description.trim() && rooms.length === 0)}
          className="w-full gap-2"
          size="lg"
        >
          {analyzing ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> {lang === 'pl' ? 'Analizuje projekt...' : 'Analyzing project...'}</>
          ) : (
            <><Calculator className="w-4 h-4" /> {lang === 'pl' ? 'Analizuj projekt i oblicz o\u015bwietlenie' : 'Analyze project & calculate lighting'}</>
          )}
        </Button>
      </div>
    );
  }

  // ===== LIST VIEW =====
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {lang === 'pl' ? 'Analizator projektow' : 'Project Analyzer'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {lang === 'pl'
              ? 'Czytaj projekty budowlane, obliczaj o\u015bwietlenie wg norm PN-EN 12464, dobieraj produkty'
              : 'Read building projects, calculate lighting per PN-EN 12464, match products'}
          </p>
        </div>
        <Button onClick={() => { setView('create'); setDescription(''); setRooms([]); }} className="gap-1.5">
          <Plus className="w-4 h-4" />
          {lang === 'pl' ? 'Nowa analiza' : 'New Analysis'}
        </Button>
      </div>

      {/* Norm info card */}
      <Card className="border border-border bg-muted/20">
        <CardContent className="p-4 flex items-start gap-3">
          <FileText className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <p className="font-medium text-foreground mb-1">
              {lang === 'pl' ? 'Obliczenia zgodne z normami:' : 'Calculations comply with standards:'}
            </p>
            <p>PN-EN 12464-1:2021 ({lang === 'pl' ? 'O\u015bwietlenie miejsc pracy we wn\u0119trzach' : 'Lighting of indoor work places'})</p>
            <p>PN-EN 12464-2:2014 ({lang === 'pl' ? 'O\u015bwietlenie miejsc pracy na zewn\u0105trz' : 'Lighting of outdoor work places'})</p>
            <p className="mt-1">
              {lang === 'pl'
                ? `${Object.keys(norms?.norms || {}).length} typow pomieszczen | ${Object.keys(norms?.convection_types || {}).length} typow konwekcji | ${Object.keys(norms?.style_families || {}).length} rodzin stylistycznych`
                : `${Object.keys(norms?.norms || {}).length} room types | ${Object.keys(norms?.convection_types || {}).length} convection types | ${Object.keys(norms?.style_families || {}).length} style families`}
            </p>
          </div>
        </CardContent>
      </Card>

      {analyses.length === 0 ? (
        <Card className="border border-border">
          <CardContent className="py-16 text-center">
            <Building2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-sm text-muted-foreground mb-4">
              {lang === 'pl'
                ? 'Brak analiz projektow. Stworz pierwsza analize!'
                : "No project analyses yet. Create your first analysis!"}
            </p>
            <Button onClick={() => { setView('create'); setDescription(''); setRooms([]); }} className="gap-1.5">
              <Calculator className="w-4 h-4" />
              {lang === 'pl' ? 'Analizuj projekt' : 'Analyze project'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {analyses.map(a => {
            const res = a.result || {};
            const sum = res.summary || {};
            return (
              <Card
                key={a.id}
                className="border border-border hover:border-border/80 transition-colors cursor-pointer group"
                onClick={() => {
                  setSelectedAnalysis(a);
                  setExpandedRooms({});
                  // Choose view based on whether this is a document analysis
                  setView(a.document_filename ? 'document_report' : 'detail');
                }}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">
                        {(a.description || '').substring(0, 60) || (lang === 'pl' ? 'Analiza projektu' : 'Project analysis')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(a.created_at).toLocaleDateString(lang === 'pl' ? 'pl-PL' : 'en-US')}
                        {a.document_filename && (
                          <span className="ml-2 text-primary">{a.document_filename}</span>
                        )}
                      </p>
                    </div>
                    <Button
                      variant="ghost" size="sm"
                      className="opacity-0 group-hover:opacity-100 h-7 w-7 p-0"
                      onClick={e => { e.stopPropagation(); handleDelete(a.id); }}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {a.document_filename ? (
                      <>
                        <Badge variant="secondary" className="text-xs">
                          <FileText className="w-3 h-3 mr-1" />
                          {a.document_type || 'dokument'}
                        </Badge>
                        {res?.compliance?.overall_status && (
                          <Badge variant="outline" className={`text-xs ${
                            res.compliance.overall_status === 'compliant' ? 'text-green-400 border-green-400/30' :
                            res.compliance.overall_status === 'non_compliant' ? 'text-red-400 border-red-400/30' :
                            'text-amber-400 border-amber-400/30'
                          }`}>
                            {res.compliance.overall_status === 'compliant' ? (lang === 'pl' ? 'Zgodny' : 'Compliant') :
                             res.compliance.overall_status === 'non_compliant' ? (lang === 'pl' ? 'Niezgodny' : 'Non-compliant') :
                             (lang === 'pl' ? 'Czesciowo' : 'Partial')}
                          </Badge>
                        )}
                        {res?.compliance?.score != null && (
                          <Badge variant="secondary" className="text-xs">
                            {res.compliance.score}/100
                          </Badge>
                        )}
                      </>
                    ) : (
                      <>
                        <Badge variant="secondary" className="text-xs">
                          <Building2 className="w-3 h-3 mr-1" />
                          {res.room_count || 0} {lang === 'pl' ? 'pom.' : 'rooms'}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          <Lightbulb className="w-3 h-3 mr-1" />
                          {sum.total_fixtures || 0} {lang === 'pl' ? 'opraw' : 'fixtures'}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          <Zap className="w-3 h-3 mr-1" />
                          {sum.total_watts || 0} W
                        </Badge>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
